"use client";

import { useCallback, useEffect, useState } from "react";
import FilterBlock from "./filter";
import Image from "next/image";
import moment from "moment";
import { Link } from "@/i18n/navigation";
import { FaArrowLeftLong, FaArrowRightLong } from "react-icons/fa6";

type OurBlogMainContentProps = {
  categories: any;
  blogs: any;
  locale: string;
};

const OurBlogMainContent = ({
  categories,
  blogs,
  locale,
}: OurBlogMainContentProps) => {
  const [filter, setFilter] = useState<string>("All");
  const [keyword, setKeyword] = useState<string>("");
  const [filteredBlogs, setFilteredBlogs] = useState<any>(blogs);

  const getBlogs = useCallback(() => {
    if (filter === "All") {
      setFilteredBlogs(blogs);
    } else {
      setFilteredBlogs(
        blogs?.filter((blog) =>
          blog?.attributes?.categories?.data?.some(
            (d) => d?.attributes?.name === filter
          )
        )
      );
    }
  }, [filter, blogs]);

  const filterByKeyword = useCallback(() => {
    if (keyword) {
      setFilteredBlogs(
        blogs?.filter((blog) =>
          blog?.attributes?.title?.toLowerCase().includes(keyword.toLowerCase())
        )
      );
    } else {
      setFilteredBlogs(blogs);
    }
  }, [keyword, blogs]);

  useEffect(() => {
    filterByKeyword();
  }, [filterByKeyword, keyword]);

  useEffect(() => {
    getBlogs();
  }, [filter, getBlogs]);

  const getFormattedDate = (timestamp: string) => {
    const date = moment(timestamp);

    const formattedDate = date.format("DD MMM YYYY");

    return formattedDate;
  };

  return (
    <div className="my-12 md:my-24">
      <FilterBlock
        categories={categories}
        filter={filter}
        setFilter={setFilter}
        keyword={keyword}
        setKeyword={setKeyword}
        searchPlaceHolder={
          locale === "en" ? "Search blogs..." : "البحث في المدونات..."
        }
        locale={locale}
      />
      <div className="container mx-auto px-5">
        {filteredBlogs.length ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10  pt-[50px]">
            {filteredBlogs?.map((blog: any, index: number) => {
              return (
                <Link
                  href={`/our-blogs/${blog?.attributes?.slug}`}
                  key={index}
                  locale={locale}
                >
                  <article className="bg-white p-4 rounded-xl shadow-sm">
                    <div className="relative h-[300px] w-full rounded-xl overflow-hidden">
                      <Image
                        src={
                          blog?.attributes?.featuredImage?.data?.attributes?.url
                        }
                        alt={
                          blog?.attributes?.featuredImage?.data?.attributes
                            ?.alternativeText || "Blog Featured Image"
                        }
                        fill
                      />
                      <div className="absolute top-4 left-4 flex items-center gap-2">
                        {blog?.attributes?.categories?.data?.map(
                          (d: any, index: number) => {
                            return (
                              <div
                                className="bg-emerald-500 text-white py-px px-2 rounded-md capitalize even:bg-orange-400"
                                key={index}
                              >
                                {d?.attributes?.name}
                              </div>
                            );
                          }
                        )}
                      </div>
                    </div>
                    <div className="ml-1">
                      <p
                        className={`my-2 text-[#9b9b9b] ${
                          locale === "en" ? "text-left" : "text-right"
                        } `}
                        dir="ltr"
                      >
                        {getFormattedDate(blog?.attributes?.updatedAt)}
                      </p>
                      <h3 className="text-xl font-bold">
                        {blog?.attributes?.title}
                      </h3>
                    </div>
                  </article>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="w-full flex flex-col items-center justify-center mx-auto pt-20">
            <div className="relative h-[100px] w-[100px]">
              <Image
                src="/assets/icons/empty.png"
                alt="no blog icon"
                fill
                className="object-cover"
              />
            </div>
            <p className="text-[#333] text-2xl pt-5">
              {locale === "en"
                ? "Currently we don't have any blog! Please check back later."
                : "لم يتم العثور على مدونات"}
            </p>
            <Link
              href="/"
              locale={locale}
              className="flex items-center gap-2 bg-[#5a558e] text-white py-3 px-6 rounded-md shadow-sm mt-4 hover:bg-[#5a558e]/80 transition-all duration-300 ease-linear"
            >
              {locale === "en" ? <FaArrowLeftLong /> : <FaArrowRightLong />}
              {locale === "en" ? "Back to Home" : "العودة للرئيسية"}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default OurBlogMainContent;
